if (!customElements.get('article-media-gallery')) {
  customElements.define(
    'article-media-gallery',
    class ArticleMediaGallery extends HTMLElement {
      connectedCallback() {
        if (this.dataset.initialized === 'true') return;
        this.dataset.initialized = 'true';

        this.article = this.closest('.article-social');
        this.content = this.article?.querySelector('.article-social__content');
        this.slidesContainer = this.querySelector('[data-gallery-slides]');
        this.controls = this.querySelector('[data-gallery-controls]');
        this.thumbnails = this.querySelector('[data-gallery-thumbnails]');
        this.count = this.querySelector('[data-gallery-count]');

        if (!this.content || !this.slidesContainer) return;

        this.items = [];
        this.markers = [];
        this.activeIndex = 0;
        this.scrollTicking = false;

        this.header = document.getElementById('shopify-section-header');
        this.syncStickyHeaderOffset = () => {
          if (!this.article || !this.header) return;

          const headerIsSticky = this.header.classList.contains('shopify-section-header-sticky');
          const headerIsHidden = this.header.classList.contains('shopify-section-header-hidden');
          const menuIsOpen = this.header.classList.contains('menu-open');
          const headerIsVisible = headerIsSticky && (!headerIsHidden || menuIsOpen);
          const headerHeight = headerIsVisible ? Math.ceil(this.header.getBoundingClientRect().height) : 0;

          this.article.style.setProperty('--article-sticky-header-offset', `${headerHeight}px`);
        };

        if (this.header) {
          this.headerObserver = new MutationObserver(this.syncStickyHeaderOffset);
          this.headerObserver.observe(this.header, {
            attributes: true,
            attributeFilter: ['class']
          });

          if ('ResizeObserver' in window) {
            this.headerResizeObserver = new ResizeObserver(this.syncStickyHeaderOffset);
            this.headerResizeObserver.observe(this.header);
          }

          this.syncStickyHeaderOffset();
        }

        const primarySlide = this.slidesContainer.querySelector('[data-gallery-primary]');
        const primaryImage = primarySlide?.querySelector('img');

        if (primarySlide && primaryImage) {
          this.items.push({
            key: this.imageKey(primaryImage.currentSrc || primaryImage.src),
            slide: primarySlide,
            alt: primaryImage.alt || 'Article image'
          });
        }

        Array.from(this.content.querySelectorAll('img')).forEach((sourceImage) => {
          const source = sourceImage.currentSrc || sourceImage.src || sourceImage.dataset.src;
          if (!source) return;

          const figure = sourceImage.closest('figure');
          const paragraph = sourceImage.closest('p');
          const caption = figure?.querySelector('figcaption')?.textContent?.trim() || '';
          const alt = sourceImage.alt?.trim() || caption || `Article image ${this.items.length + 1}`;
          const key = this.imageKey(source);
          let itemIndex = this.items.findIndex((item) => item.key === key);

          if (itemIndex < 0) {
            const slide = document.createElement('figure');
            slide.className = 'article-social__media';
            slide.dataset.gallerySlide = '';
            slide.hidden = true;

            const galleryImage = document.createElement('img');
            galleryImage.src = source;
            galleryImage.alt = alt;
            galleryImage.loading = 'lazy';
            galleryImage.decoding = 'async';

            const sourceSet = sourceImage.getAttribute('srcset') || sourceImage.dataset.srcset;
            if (sourceSet) galleryImage.setAttribute('srcset', sourceSet);

            const sizes = sourceImage.getAttribute('sizes');
            if (sizes) galleryImage.setAttribute('sizes', sizes);

            slide.appendChild(galleryImage);

            if (caption) {
              const galleryCaption = document.createElement('figcaption');
              galleryCaption.className = 'article-social__slide-caption';
              galleryCaption.textContent = caption;
              slide.appendChild(galleryCaption);
            }

            this.slidesContainer.appendChild(slide);
            itemIndex = this.items.length;
            this.items.push({ key, slide, alt });
          }

          const marker = document.createElement('span');
          marker.className = 'article-social__image-marker';
          marker.dataset.galleryIndex = String(itemIndex);
          marker.setAttribute('aria-hidden', 'true');

          const paragraphContainsOnlyImage =
            paragraph &&
            paragraph.textContent.trim() === '' &&
            paragraph.querySelectorAll('img').length === 1;
          const replaceTarget = figure || (paragraphContainsOnlyImage ? paragraph : sourceImage);
          replaceTarget.replaceWith(marker);
          this.markers.push(marker);
        });

        const placeholder = this.slidesContainer.querySelector('[data-gallery-placeholder]');
        if (this.items.length > 0 && placeholder) placeholder.remove();
        if (this.items.length === 0) return;

        this.buildThumbnails();
        this.activate(0);

        if (this.items.length > 1) {
          this.controls.hidden = false;
          this.thumbnails.hidden = false;
          this.count.hidden = false;

          this.querySelector('[data-gallery-previous]')?.addEventListener('click', () => {
            this.activate((this.activeIndex - 1 + this.items.length) % this.items.length);
          });
          this.querySelector('[data-gallery-next]')?.addEventListener('click', () => {
            this.activate((this.activeIndex + 1) % this.items.length);
          });

          this.onScroll = () => {
            if (this.scrollTicking) return;
            this.scrollTicking = true;
            window.requestAnimationFrame(() => {
              this.syncToReadingPosition();
              this.scrollTicking = false;
            });
          };
          window.addEventListener('scroll', this.onScroll, { passive: true });
          this.syncToReadingPosition();
        }
      }

      disconnectedCallback() {
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
        this.headerObserver?.disconnect();
        this.headerResizeObserver?.disconnect();
      }

      imageKey(source) {
        try {
          return new URL(source, window.location.href).pathname;
        } catch (_error) {
          return source;
        }
      }

      buildThumbnails() {
        this.thumbnails.replaceChildren();
        this.items.forEach((item, index) => {
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'article-social__gallery-thumbnail';
          button.dataset.galleryThumbnail = String(index);
          button.setAttribute('aria-label', `Show image ${index + 1}: ${item.alt}`);

          const image = item.slide.querySelector('img')?.cloneNode();
          if (image) {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.loading = 'lazy';
            image.alt = '';
            button.appendChild(image);
          }

          button.addEventListener('click', () => this.activate(index));
          this.thumbnails.appendChild(button);
        });
      }

      activate(index) {
        if (!Number.isInteger(index) || index < 0 || index >= this.items.length) return;
        this.activeIndex = index;

        this.items.forEach((item, itemIndex) => {
          const isActive = itemIndex === index;
          item.slide.hidden = !isActive;
          item.slide.classList.toggle('is-active', isActive);
        });

        this.querySelectorAll('[data-gallery-thumbnail]').forEach((thumbnail, itemIndex) => {
          const isActive = itemIndex === index;
          thumbnail.classList.toggle('is-active', isActive);
          thumbnail.setAttribute('aria-current', isActive ? 'true' : 'false');
        });

        if (this.count) {
          this.count.textContent = `${String(index + 1).padStart(2, '0')} / ${String(this.items.length).padStart(2, '0')}`;
        }
      }

      syncToReadingPosition() {
        if (!this.markers.length || !window.matchMedia('(min-width: 990px)').matches) return;

        const triggerLine = window.innerHeight * 0.44;
        let readingIndex = 0;

        this.markers.forEach((marker) => {
          if (marker.getBoundingClientRect().top <= triggerLine) {
            readingIndex = Number(marker.dataset.galleryIndex);
          }
        });

        if (readingIndex !== this.activeIndex) this.activate(readingIndex);
      }
    }
  );
}

